/**
 * API imperativa global para componentes que no son puramente declarativos.
 * Uso: window.ExtUI.toast.show({ message, kind })
 */
export interface ToastOptions {
  message: string;
  kind?: 'success' | 'danger' | 'warning' | 'info';
  duration?: number;
}

export type ExtUIHookFn = (ctx: unknown) => unknown | Promise<unknown>;

export interface ExtUIRegistry {
  toast: {
    show(opts: ToastOptions): string;
    dismiss(id: string): void;
  };
  /**
   * Dispatcha un hook registrado por satellite modules (ej. medical_turnos
   * para inyectar paciente/turno en quick-move). Devuelve los resultados
   * de todos los handlers (uno por registro). Los errores de handlers se
   * loguean y no interrumpen el resto.
   */
  dispatchHook(name: string, ctx: unknown): Promise<unknown[]>;
  /**
   * Registra un handler para un hook-point. Multiple handlers por name
   * estan permitidos; dispatchHook los ejecuta en orden de registro.
   */
  registerHook(name: string, fn: ExtUIHookFn): void;
  // futuro: modal.confirm(), dialog.prompt(), etc.
}

const hooks: Record<string, ExtUIHookFn[]> = {};

// Stub inicial - los metodos reales los registran los componentes en UI-1.
const registry: ExtUIRegistry = {
  toast: {
    show(opts: ToastOptions): string {
      // eslint-disable-next-line no-console
      console.warn('[ExtUI] toast.show llamado antes de que ext-toast cargue', opts);
      return '';
    },
    dismiss(_id: string): void {
      // eslint-disable-next-line no-console
      console.warn('[ExtUI] toast.dismiss llamado antes de que ext-toast cargue');
    },
  },
  async dispatchHook(name: string, ctx: unknown): Promise<unknown[]> {
    const fns = hooks[name] || [];
    const results: unknown[] = [];
    for (const fn of fns) {
      try {
        results.push(await fn(ctx));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[ExtUI.hook]', name, e);
      }
    }
    return results;
  },
  registerHook(name: string, fn: ExtUIHookFn): void {
    if (!hooks[name]) hooks[name] = [];
    hooks[name].push(fn);
  },
};

if (typeof window !== 'undefined') {
  (window as unknown as { ExtUI: ExtUIRegistry }).ExtUI = registry;
}

export default registry;
